import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
} from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { getApiBaseUrl } from "../../../config/api";
import { ResultsPageContainer, ResultsPageHeader, KpiCard, ResultsSectionPaper, ResponsiveTableWrap, EmptyResultsRow } from "./resultsUi";
import {
    TermRecordsScopeAlert,
    TermRecordsScopeTabs,
} from "../../../components/term/TermRecordsScopeTabs";
import { useTermRecordsScope } from "../../../hooks/useTermRecordsScope";
import { buildTermRecordsQuery } from "../../../utils/termRecordsScope";

const LEVEL_FILTERS = ["All", "Primary", "Secondary", "University"];

const ExamResultsOverview = () => {
    const navigate = useNavigate();
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = currentUser?._id;
    const { recordsScope, setRecordsScope, currentTermLabel } = useTermRecordsScope(schoolId);

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [levelFilter, setLevelFilter] = useState("All");

    useEffect(() => {
        if (!schoolId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${getApiBaseUrl()}/SchoolResultsOverview/${schoolId}`, {
                    params: buildTermRecordsQuery({ recordsScope }),
                });
                if (!cancelled) {
                    setRows(Array.isArray(data) ? data : []);
                    setError("");
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.message || "Could not load results overview");
                    setRows([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [schoolId, recordsScope]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((row) => {
            if (levelFilter !== "All" && row.educationLevel !== levelFilter) return false;
            if (!q) return true;
            return (
                String(row.sclassName || "").toLowerCase().includes(q) ||
                String(row.levelCode || "").toLowerCase().includes(q) ||
                String(row.examType || "").toLowerCase().includes(q)
            );
        });
    }, [rows, search, levelFilter]);

    const summary = useMemo(() => {
        const totalClasses = filtered.length;
        const totalStudents = filtered.reduce((s, r) => s + (Number(r.studentCount) || 0), 0);
        const withMarks = filtered.reduce((s, r) => s + (Number(r.studentsWithMarks) || 0), 0);
        const avgOfAvgs =
            totalClasses > 0
                ? filtered.reduce((s, r) => s + (Number(r.averageMark) || 0), 0) / totalClasses
                : 0;
        const examClasses = filtered.filter((r) => r.isExamClass).length;
        return { totalClasses, totalStudents, withMarks, avgOfAvgs, examClasses };
    }, [filtered]);

    return (
        <ResultsPageContainer>
            <ResultsPageHeader
                title="Exam & Results Hub"
                subtitle="Review class performance, approve teacher submissions, and generate report cards at end of term."
                action={
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <Button variant="contained" startIcon={<FactCheckOutlinedIcon />} onClick={() => navigate("/Admin/exams/review")}>
                            Review marks
                        </Button>
                        <Button variant="outlined" startIcon={<ArticleOutlinedIcon />} onClick={() => navigate("/Admin/students")}>
                            Students & report cards
                        </Button>
                    </Stack>
                }
            />

            <TermRecordsScopeTabs value={recordsScope} onChange={setRecordsScope} />
            <TermRecordsScopeAlert
                scope={recordsScope}
                currentTermLabel={currentTermLabel}
                sectionLabel="results overview"
            />

            {error ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard title="Classes" value={summary.totalClasses} icon={SchoolOutlinedIcon} color="primary" loading={loading} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard title="Students" value={summary.totalStudents} icon={AssessmentOutlinedIcon} color="info" loading={loading} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        title="With marks recorded"
                        value={summary.withMarks}
                        icon={TrendingUpOutlinedIcon}
                        color="success"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        title="Exam classes"
                        value={summary.examClasses}
                        icon={EmojiEventsOutlinedIcon}
                        color="warning"
                        loading={loading}
                    />
                </Grid>
            </Grid>

            <ResultsSectionPaper title="Classes">
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
                    <TextField
                        size="small"
                        placeholder="Search class…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: { xs: "100%", sm: 220 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Level</InputLabel>
                        <Select label="Level" value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
                            {LEVEL_FILTERS.map((l) => (
                                <MenuItem key={l} value={l}>
                                    {l}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <ResponsiveTableWrap>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Class</TableCell>
                                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>Level</TableCell>
                                <TableCell align="right">Students</TableCell>
                                <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" } }}>
                                    With marks
                                </TableCell>
                                <TableCell align="right">Avg %</TableCell>
                                <TableCell align="right" sx={{ display: { xs: "none", lg: "table-cell" } }}>
                                    Pass rate
                                </TableCell>
                                <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>Top student</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <EmptyResultsRow colSpan={8} message="Loading…" />
                            ) : filtered.length === 0 ? (
                                <EmptyResultsRow colSpan={8} message="No classes match your filters." />
                            ) : (
                                filtered.map((row) => (
                                    <TableRow key={row.classId} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                                                <Box component="span" sx={{ fontWeight: 600 }}>
                                                    {row.sclassName}
                                                </Box>
                                                {row.isExamClass ? (
                                                    <Chip label={row.examType || "Exam"} size="small" color="warning" />
                                                ) : null}
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                                            <Chip label={row.educationLevel} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell align="right">{row.studentCount}</TableCell>
                                        <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" } }}>
                                            {row.studentsWithMarks}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.educationLevel === "University"
                                                ? "—"
                                                : `${Number(row.averageMark || 0).toFixed(1)}%`}
                                        </TableCell>
                                        <TableCell align="right" sx={{ display: { xs: "none", lg: "table-cell" } }}>
                                            {row.educationLevel === "University" ? "—" : `${row.passRate}%`}
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                                            {row.topStudentName || "—"}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                                                <Tooltip title="Published results records">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() =>
                                                            navigate(`/Admin/exams/records/${row.classId}`)
                                                        }
                                                    >
                                                        Records
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip title="Live class results (includes drafts)">
                                                    <Button
                                                        size="small"
                                                        onClick={() => navigate(`/Admin/classes/results/${row.classId}`)}
                                                    >
                                                        Live
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip title="Class details">
                                                    <Button
                                                        size="small"
                                                        onClick={() => navigate(`/Admin/classes/class/${row.classId}`)}
                                                    >
                                                        Class
                                                    </Button>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </ResponsiveTableWrap>
            </ResultsSectionPaper>
        </ResultsPageContainer>
    );
};

export default ExamResultsOverview;
